"use client";

import { useEffect, useState } from "react";
import { ResponsiveDialog } from "@/components/ui/responsive-dialog";
import { fetchCreditTransactions, type CreditTransaction } from "@/lib/api";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Sparkles,
  Loader2,
  Crown,
  Gift,
  CreditCard,
  Clock,
  ShieldCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PAGE_SIZE = 30;

const TYPE_LABELS: Record<string, string> = {
  generation: "Генерация",
  usage: "Эффект",
  assistant: "AI-ассистент",
  payment: "Оплата",
  subscription_renewal: "Продление подписки",
  subscription_credit: "Подписка",
  refund: "Возврат",
  promo_code: "Промокод",
  admin_grant: "Бонус от администратора",
  admin_deduction: "Списание администратором",
  expiry: "Истечение пакета",
  bonus: "Бонусные кредиты",
  promo: "Промокод",
};

const TYPE_ICONS: Record<string, React.ReactNode> = {
  generation: <Sparkles className="h-4 w-4" />,
  usage: <Sparkles className="h-4 w-4" />,
  assistant: <Sparkles className="h-4 w-4" />,
  payment: <CreditCard className="h-4 w-4" />,
  subscription_renewal: <Crown className="h-4 w-4" />,
  subscription_credit: <Crown className="h-4 w-4" />,
  refund: <ArrowUpCircle className="h-4 w-4" />,
  promo_code: <Gift className="h-4 w-4" />,
  promo: <Gift className="h-4 w-4" />,
  admin_grant: <ShieldCheck className="h-4 w-4" />,
  admin_deduction: <ShieldCheck className="h-4 w-4" />,
  expiry: <Clock className="h-4 w-4" />,
  bonus: <Gift className="h-4 w-4" />,
};

/** Human label for the credit_ledger.wallet column.
 *  - subscription = списано/начислено в подписочный кошелёк
 *  - purchased    = в докупленные кредиты
 *  - split        = списано из обоих сразу (когда в одном кошельке
 *                   не хватило и добор пошёл из второго) */
function walletLabel(wallet: string): string {
  switch (wallet) {
    case "subscription": return "подписка";
    case "purchased": return "пакет";
    case "split": return "пакет";
    default: return wallet;
  }
}
function walletTooltip(wallet: string): string {
  switch (wallet) {
    case "subscription": return "Кредиты из подписочного пакета";
    case "purchased": return "Кредиты из вашего пакета";
    case "split": return "Списано из вашего пакета";
    default: return wallet;
  }
}

function formatRelative(iso: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "только что";
  if (diffMin < 60) return `${diffMin} мин назад`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} ч назад`;
  return date.toLocaleDateString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export function TransactionsModal({ open, onOpenChange }: Props) {
  const [items, setItems] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setOffset(0);
    fetchCreditTransactions(PAGE_SIZE, 0)
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setHasMore(res.has_more);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Не удалось загрузить историю");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const loadMore = async () => {
    setLoading(true);
    try {
      const next = offset + PAGE_SIZE;
      const res = await fetchCreditTransactions(PAGE_SIZE, next);
      setItems((prev) => [...prev, ...res.items]);
      setHasMore(res.has_more);
      setOffset(next);
    } catch {
      setError("Не удалось подгрузить");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      title="История операций"
      className="max-w-[95vw] sm:max-w-[520px] p-0 h-[85vh] sm:h-auto sm:max-h-[85vh] overflow-hidden flex flex-col"
    >
      <div className="px-5 pt-5 pb-3 border-b border-border/40">
        <p className="text-[15px] font-semibold tracking-tight text-foreground">История операций</p>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          Начисления и списания кредитов
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-2 py-2">
        {loading && items.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Загрузка...
          </div>
        ) : error ? (
          <div className="text-center py-12 text-sm text-muted-foreground">
            {error}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-sm text-muted-foreground">
            Операций пока нет.
          </div>
        ) : (
          <ul className="divide-y divide-border/30">
            {items.map((tx) => (
              <TransactionRow key={tx.id} tx={tx} />
            ))}
          </ul>
        )}

        {hasMore && !loading && (
          <div className="flex justify-center py-3">
            <button
              onClick={loadMore}
              className="px-4 py-2 rounded-xl bg-muted/40 text-[12px] font-semibold text-muted-foreground hover:text-foreground hover:bg-muted/60 active:scale-[0.97] transition-all"
            >
              Загрузить ещё
            </button>
          </div>
        )}
        {loading && items.length > 0 && (
          <div className="flex justify-center py-3 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        )}
      </div>
    </ResponsiveDialog>
  );
}

function TransactionRow({ tx }: { tx: CreditTransaction }) {
  const isIncome = tx.amount > 0;
  const icon = TYPE_ICONS[tx.type] ?? (isIncome ? <ArrowUpCircle className="h-4 w-4" /> : <ArrowDownCircle className="h-4 w-4" />);
  const label = TYPE_LABELS[tx.type] ?? tx.type;
  const amountStr = isIncome ? `+${tx.amount}` : `${tx.amount}`;
  const detail = tx.description?.trim() || tx.reason?.trim();

  return (
    <li className="flex items-start gap-3 px-3 py-3">
      <div className={cn(
        "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
        isIncome ? "bg-emerald-500/10 text-emerald-500" : "bg-muted text-muted-foreground",
      )}>
        {icon}
      </div>
      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-[13px] font-semibold truncate">{label}</span>
          <span className={cn(
            "text-[13px] font-bold tabular-nums shrink-0",
            isIncome ? "text-emerald-500" : "text-foreground",
          )}>
            {amountStr}
          </span>
        </div>
        {detail && (
          <p className="text-[11px] text-muted-foreground line-clamp-2">{detail}</p>
        )}
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground/70">
          <span>{formatRelative(tx.created_at)}</span>
          {tx.wallet && (
            <>
              <span>·</span>
              <span title={walletTooltip(tx.wallet)}>{walletLabel(tx.wallet)}</span>
            </>
          )}
          <span className="ml-auto tabular-nums">баланс: {tx.balance_after}</span>
        </div>
      </div>
    </li>
  );
}
